/**
 * useTransferSubmission Hook
 * 
 * Manages transfer submission logic including validation, account updates,
 * transaction creation, and attachment uploads.
 */

import { FormEvent } from "react";
import { auth, storage } from "../../../firebase";
import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  writeBatch,
  increment,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db } from "../../../firebase";
import { Account, AttachmentHistoryEntry } from "../../../types";
import { isDemoMode } from "../../../utils/demoMode";
import { isBypassAuthEnabled } from "../../../utils/bypassAuth";
import { DemoModeMessage } from "../../../utils/demoModeMessage";

export interface TransferData {
  fromAccountId: string;
  toAccountId: string;
  amount: string;
  transferRate: string;
  date: string;
  note: string;
  attachmentFile: File | null;
  attachmentUrl: string | null;
  attachmentName: string | null;
  attachmentType: string | null;
  feeAmount?: string;
  feeAccountId?: string;
}

interface UseTransferSubmissionParams {
  workspaceId: string;
  accounts: Account[];
  transferData: TransferData;
  setTransferData: React.Dispatch<React.SetStateAction<TransferData>>;
  setTransferDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setUploadingAttachment: React.Dispatch<React.SetStateAction<boolean>>;
  setSnackbarElement: React.Dispatch<React.SetStateAction<React.ReactElement | null>>;
  setSnackbarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  transferFileInputRef: React.RefObject<HTMLInputElement | null>;
  defaultCurrency: string;
}

interface UseTransferSubmissionReturn {
  handleTransferSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
}

/**
 * Hook to manage transfer submission
 */
export function useTransferSubmission({
  workspaceId,
  accounts,
  transferData,
  setTransferData,
  setTransferDialogOpen,
  setUploadingAttachment,
  setSnackbarElement,
  setSnackbarOpen,
  transferFileInputRef,
  defaultCurrency,
}: UseTransferSubmissionParams): UseTransferSubmissionReturn {
  const handleTransferSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    
    // In demo mode, transfers cannot be created
    if (isDemoMode()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Transfers cannot be created. Please log in to create transfers." />
      );
      setSnackbarOpen(true);
      setTransferDialogOpen(false);
      setTransferData({
        fromAccountId: "",
        toAccountId: "",
        amount: "",
        transferRate: "",
        date: new Date().toISOString().split("T")[0],
        note: "",
        attachmentFile: null,
        attachmentUrl: null,
        attachmentName: null,
        attachmentType: null,
      });
      if (transferFileInputRef.current) {
        transferFileInputRef.current.value = "";
      }
      return;
    }
    
    if (!workspaceId || !auth.currentUser) return;

    if (transferData.fromAccountId === transferData.toAccountId) {
      alert("Cannot transfer to the same account");
      return;
    }

    const amount = parseFloat(transferData.amount);
    if (isNaN(amount) || amount <= 0) {
      alert("Please enter a valid amount");
      return;
    }

    try {
      const fromAccount = accounts.find(
        (acc) => acc.id === transferData.fromAccountId
      );
      const toAccount = accounts.find(
        (acc) => acc.id === transferData.toAccountId
      );

      if (!fromAccount || !toAccount) {
        alert("Account not found");
        return;
      }

      // Check if currencies differ
      // Note: Balance check removed to allow transfers even with insufficient funds (negative balances allowed)
      const currenciesDiffer = fromAccount.currency !== toAccount.currency;
      let transferRate: number | undefined;

      if (currenciesDiffer) {
        // Require transfer rate when currencies differ
        if (!transferData.transferRate || transferData.transferRate.trim() === "") {
          alert("Please enter an exchange rate for currency conversion");
          return;
        }
        transferRate = parseFloat(transferData.transferRate);
        if (isNaN(transferRate) || transferRate <= 0) {
          alert("Please enter a valid exchange rate");
          return;
        }
      }

      // Validate account amounts are valid numbers
      const fromAccountAmount = parseFloat(String(fromAccount.amount || 0));
      const toAccountAmount = parseFloat(String(toAccount.amount || 0));
      
      console.log("Transfer validation:", {
        fromAccount: {
          id: fromAccount.id,
          name: fromAccount.accountName,
          amount: fromAccount.amount,
          parsedAmount: fromAccountAmount,
          currency: fromAccount.currency,
        },
        toAccount: {
          id: toAccount.id,
          name: toAccount.accountName,
          amount: toAccount.amount,
          parsedAmount: toAccountAmount,
          currency: toAccount.currency,
        },
        transferAmount: amount,
        currenciesDiffer,
        transferRate,
      });
      
      if (isNaN(fromAccountAmount) || !isFinite(fromAccountAmount)) {
        alert(`Invalid balance for source account "${fromAccount.accountName}". Current balance: ${fromAccount.amount}. Please refresh and try again.`);
        return;
      }
      
      if (isNaN(toAccountAmount) || !isFinite(toAccountAmount)) {
        alert(`Invalid balance for destination account "${toAccount.accountName}". Current balance: ${toAccount.amount}. Please refresh and try again.`);
        return;
      }

      // Use batch write for atomic updates
      const batch = writeBatch(db);

      // Calculate amount to add to toAccount (convert if currencies differ)
      const toAmount = currenciesDiffer && transferRate ? amount * transferRate : amount;
      
      // Validate calculated amounts are valid
      if (isNaN(toAmount) || !isFinite(toAmount)) {
        alert(`Invalid transfer calculation. Amount: ${amount}, Rate: ${transferRate}, Calculated: ${toAmount}. Please check the exchange rate and try again.`);
        return;
      }

      // Calculate new balances
      const newFromAmount = fromAccountAmount - amount;
      const newToAmount = toAccountAmount + toAmount;
      
      console.log("Calculated balances:", {
        newFromAmount,
        newToAmount,
        isFiniteFrom: isFinite(newFromAmount),
        isFiniteTo: isFinite(newToAmount),
      });
      
      if (isNaN(newFromAmount) || !isFinite(newFromAmount) || isNaN(newToAmount) || !isFinite(newToAmount)) {
        alert(`Invalid balance calculation. From: ${newFromAmount}, To: ${newToAmount}. Please refresh and try again.`);
        return;
      }

      // Ensure values are proper numbers (not Infinity, -Infinity, or NaN)
      const finalFromAmount = Number(newFromAmount);
      const finalToAmount = Number(newToAmount);
      
      if (!isFinite(finalFromAmount) || !isFinite(finalToAmount)) {
        alert(`Invalid final balance values. From: ${finalFromAmount}, To: ${finalToAmount}. Please refresh and try again.`);
        return;
      }

      // Check if accounts are archived (can't use archived accounts in transfers)
      if (fromAccount.isArchived) {
        alert(`Cannot transfer from archived account "${fromAccount.accountName}". Please unarchive it first.`);
        return;
      }
      if (toAccount.isArchived) {
        alert(`Cannot transfer to archived account "${toAccount.accountName}". Please unarchive it first.`);
        return;
      }

      // Update account balances
      const fromAccountRef = doc(db, "workspaces", workspaceId, "accounts", fromAccount.id);
      const toAccountRef = doc(db, "workspaces", workspaceId, "accounts", toAccount.id);
      
      console.log("Updating accounts:", {
        fromAccountId: fromAccount.id,
        fromAmount: finalFromAmount,
        toAccountId: toAccount.id,
        toAmount: finalToAmount,
      });
      
      batch.update(fromAccountRef, { amount: finalFromAmount });
      batch.update(toAccountRef, { amount: finalToAmount });

      // Get userId - use "bypass-user" in bypass auth mode
      const transferUserId = auth.currentUser?.uid || (isBypassAuthEnabled() ? "bypass-user" : undefined);
      if (!transferUserId) {
        throw new Error("User not authenticated");
      }

      // Create transfer transaction (same structure as Activity page)
      const transactionsRef = collection(db, "workspaces", workspaceId, "transactions");
      const transferTransactionRef = doc(transactionsRef);
      
      // Build transaction data - only include transferRate if it's defined (not undefined)
      // Parse date from YYYY-MM-DD format
      const transferDate = transferData.date ? new Date(transferData.date) : new Date();
      const transactionData: {
        userId: string;
        type: "transfer";
        amount: number;
        currency: string;
        date: Timestamp;
        note: string;
        fromAccountId: string;
        fromAccountName: string;
        toAccountId: string;
        toAccountName: string;
        createdAt: ReturnType<typeof serverTimestamp>;
        transferRate?: number;
      } = {
        userId: transferUserId,
        type: "transfer",
        amount: amount, // Store original amount in fromAccount currency
        currency: fromAccount.currency,
        date: Timestamp.fromDate(transferDate),
        note: transferData.note || "",
        fromAccountId: fromAccount.id,
        fromAccountName: fromAccount.accountName, // Store account name for deleted accounts
        toAccountId: toAccount.id,
        toAccountName: toAccount.accountName, // Store account name for deleted accounts
        createdAt: serverTimestamp(),
      };
      
      // Only include transferRate if it's defined (when currencies differ)
      if (transferRate !== undefined) {
        transactionData.transferRate = transferRate;
      }
      
      batch.set(transferTransactionRef, transactionData);

      // Handle fee if present
      if (transferData.feeAmount && transferData.feeAccountId) {
        const feeAmount = parseFloat(transferData.feeAmount);
        if (!isNaN(feeAmount) && feeAmount > 0) {
          const feeAccount = accounts.find((acc) => acc.id === transferData.feeAccountId);
          
          if (!feeAccount) {
            throw new Error("Fee account not found");
          }

          // Update fee account balance (subtract fee)
          const feeAccountRef = doc(db, "workspaces", workspaceId, "accounts", feeAccount.id);
          batch.update(feeAccountRef, { amount: increment(-feeAmount) });

          // Create fee expense transaction
          const feeTransactionData: any = {
            userId: transferUserId,
            title: "Transfer Fee",
            category: "Transfer Fee",
            type: "expense",
            amount: feeAmount,
            currency: feeAccount.currency,
            rate: 1, // Fee is always in fee account currency
            amountInDefaultCurrency: feeAmount, // Same as amount since rate is 1
            defaultCurrencyAtTime: defaultCurrency,
            isIncome: false,
            date: Timestamp.fromDate(transferDate),
            note: `Fee for transfer from ${fromAccount.accountName} to ${toAccount.accountName}`,
            accountId: feeAccount.id,
            accountName: feeAccount.accountName,
            createdAt: serverTimestamp(),
            transferFeeFor: transferTransactionRef.id, // Link to transfer transaction
          };

          const feeTransactionRef = doc(transactionsRef);
          batch.set(feeTransactionRef, feeTransactionData);
        }
      }

      await batch.commit();

      // Upload attachment for new transfers after transaction is created
      if (transferData.attachmentFile && transferTransactionRef) {
        setUploadingAttachment(true);
        try {
          // Verify transaction was created
          const transactionDoc = await getDoc(transferTransactionRef);
          if (!transactionDoc.exists()) {
            throw new Error("Transaction document was not created");
          }
          
          // Ensure user is in workspace members subcollection before uploading
          // Skip in bypass auth mode (dev workspace allows writes without membership)
          if (auth.currentUser && !isBypassAuthEnabled()) {
            const currentUserId = auth.currentUser.uid;
            const memberRef = doc(db, "workspaces", workspaceId, "members", currentUserId);
            const memberSnap = await getDoc(memberRef);
            
            if (!memberSnap.exists()) {
              // Check if user is the workspace owner
              const workspaceRef = doc(db, "workspaces", workspaceId);
              const workspaceSnap = await getDoc(workspaceRef);
              const workspaceData = workspaceSnap.data();
              const isOwner = workspaceSnap.exists() && workspaceData?.ownerId === currentUserId;
              
              if (isOwner) {
                // Add owner to members subcollection
                await setDoc(memberRef, {
                  role: "owner",
                  joinedAt: serverTimestamp(),
                });
              } else {
                throw new Error("User is not a member of this workspace. Please ask the workspace owner to add you as a member.");
              }
            }
          } else if (!auth.currentUser && !isBypassAuthEnabled()) {
            throw new Error("User is not authenticated. Please log in again.");
          }
          
          // Sanitize filename to remove special characters that might cause issues
          const sanitizedFileName = transferData.attachmentFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
          const attachmentPath = `workspaces/${workspaceId}/transactions/${transferTransactionRef.id}/attachments/${Date.now()}_${sanitizedFileName}`;
          
          const attachmentRef = ref(storage, attachmentPath);
          
          // Ensure content type is normalized (jpeg not jpg)
          let contentType = transferData.attachmentType || transferData.attachmentFile.type;
          if (contentType === "image/jpg") {
            contentType = "image/jpeg";
          }
          
          // Upload with metadata to ensure content type is set correctly
          const metadata = {
            contentType: contentType,
            customMetadata: {
              originalFileName: transferData.attachmentFile.name,
              uploadedAt: new Date().toISOString(),
            },
          };
          
          await uploadBytes(attachmentRef, transferData.attachmentFile, metadata);
          const downloadURL = await getDownloadURL(attachmentRef);
          
          // Create attachment history entry for new attachment
          const newAttachmentEntry: AttachmentHistoryEntry = {
            url: downloadURL,
            name: transferData.attachmentFile.name,
            type: contentType,
            uploadedAt: Timestamp.now(),
            uploadedBy: auth.currentUser!.uid,
          };
          
          // Update transaction with attachment URL and history (use normalized type)
          await updateDoc(transferTransactionRef, {
            attachmentUrl: downloadURL,
            attachmentName: transferData.attachmentFile.name,
            attachmentType: contentType,
            attachmentHistory: [newAttachmentEntry], // Initialize history with first attachment
          });
        } catch (error) {
          console.error("Error uploading attachment:", error);
          const errorMessage = error instanceof Error ? error.message : String(error);
          const errorCode = (error as any)?.code || "unknown";
          
          // Check if it's a permission error
          if (errorCode === "storage/unauthorized" || errorMessage.includes("permission")) {
            alert(`Transfer created but failed to upload attachment due to permissions.\n\nError: ${errorMessage}\nCode: ${errorCode}\n\n⚠️ IMPORTANT: Please deploy storage rules first:\n\nRun: firebase deploy --only storage\n\nThen try uploading the attachment again by editing the transaction.`);
          } else {
            alert(`Transfer created but failed to upload attachment.\n\nError: ${errorMessage}\nCode: ${errorCode}\n\nPlease check the browser console for more details and try editing the transaction to add the attachment.`);
          }
        } finally {
          setUploadingAttachment(false);
        }
      }

      setTransferDialogOpen(false);
      setTransferData({
        fromAccountId: "",
        toAccountId: "",
        amount: "",
        transferRate: "",
        date: new Date().toISOString().split("T")[0], // Reset to today
        note: "",
        attachmentFile: null,
        attachmentUrl: null,
        attachmentName: null,
        attachmentType: null,
      });
      if (transferFileInputRef.current) {
        transferFileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Error processing transfer:", error);
      console.error("Error details:", {
        error,
        errorType: typeof error,
        errorConstructor: error?.constructor?.name,
        errorString: String(error),
        errorJSON: JSON.stringify(error, Object.getOwnPropertyNames(error)),
      });
      
      // Provide more detailed error messages
      let errorMessage = "Failed to process transfer";
      
      if (error instanceof Error) {
        const errorMsg = error.message.toLowerCase();
        
        if (errorMsg.includes("permission") || errorMsg.includes("permission-denied")) {
          errorMessage = "Permission denied. Please ensure you have access to this workspace.";
        } else if (errorMsg.includes("not-found")) {
          errorMessage = "Account not found. Please refresh the page and try again.";
        } else if (errorMsg.includes("invalid-argument") || errorMsg.includes("invalid")) {
          errorMessage = `Invalid data: ${error.message}. Please check the account balances and try again.`;
        } else if (errorMsg.includes("unavailable") || errorMsg.includes("network")) {
          errorMessage = "Network error. Please check your connection and try again.";
        } else {
          errorMessage = `Failed to process transfer: ${error.message}`;
        }
      } else if (typeof error === "object" && error !== null) {
        // Handle Firestore errors
        const firestoreError = error as { code?: string; message?: string; stack?: string };
        console.error("Firestore error code:", firestoreError.code);
        console.error("Firestore error message:", firestoreError.message);
        
        if (firestoreError.code === "permission-denied") {
          errorMessage = "Permission denied. Please ensure you have access to this workspace.";
        } else if (firestoreError.code === "not-found") {
          errorMessage = "Account not found. Please refresh the page and try again.";
        } else if (firestoreError.code === "invalid-argument") {
          errorMessage = `Invalid argument: ${firestoreError.message || "Please check the account balances and try again."}`;
        } else if (firestoreError.message) {
          errorMessage = `Failed to process transfer: ${firestoreError.message}`;
        } else if (firestoreError.code) {
          errorMessage = `Failed to process transfer (${firestoreError.code}). Please try again.`;
        }
      }
      
      alert(errorMessage);
    }
  };

  return {
    handleTransferSubmit,
  };
}
